import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { Box, CircularProgress, Dialog, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { colors, radius, shadows } from "../theme/tokens";

import { usePdfDocument, type PdfStatus } from "./use-pdf-document";

import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";

// A continuous-scroll PDF viewer. The Agreements page reads a long document,
// not a slide deck, so its reading pane scrolls through every page (Bill,
// 2026-09-07); the Programs modal keeps the paginated PdfPages. The viewer
// fills whatever height its parent gives it and scrolls inside itself: a bar
// on top carries the title, the page under the reader's eye as they scroll,
// full screen and download. Pages are drawn to one canvas each at the pane's
// width; a resize redraws them. Full screen is the same fullScreen Dialog as
// PdfPages uses, with the pages capped at a reading width and centred.

export function PdfScroll({
    url,
    title,
    onClose,
    action,
}: {
    url: string;
    title: string;
    /** Set when already inside a full-screen dialog: close button instead of expand. */
    onClose?: () => void;
    /** Rendered in the bar left of the page count (the Agreements send button). */
    action?: ReactNode;
}) {
    const { doc, status } = usePdfDocument(url);
    const [expanded, setExpanded] = useState(false);

    const viewer = (close?: () => void) => (
        <ScrollViewer
            doc={doc}
            status={status}
            title={title}
            action={action}
            downloadUrl={url}
            onExpand={close ? undefined : () => setExpanded(true)}
            onClose={close}
        />
    );

    if (onClose) return viewer(onClose);
    return (
        <>
            {viewer()}
            <Dialog open={expanded} fullScreen onClose={() => setExpanded(false)}>
                {viewer(() => setExpanded(false))}
            </Dialog>
        </>
    );
}

const PAGE_GUTTER = 16;

function ScrollViewer({
    doc,
    status,
    title,
    action,
    downloadUrl,
    onExpand,
    onClose,
}: {
    doc: PDFDocumentProxy | null;
    status: PdfStatus;
    title: string;
    action?: ReactNode;
    downloadUrl: string;
    onExpand?: () => void;
    onClose?: () => void;
}) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    const [current, setCurrent] = useState(1);
    const numPages = doc?.numPages ?? 0;
    const fullScreen = Boolean(onClose);

    // Page width follows the scroll box; full screen caps it at a reading
    // measure so a wide monitor does not stretch a letter page edge to edge.
    useEffect(() => {
        const box = scrollRef.current;
        if (!box) return;
        const measure = () => {
            const inner = box.clientWidth - 2 * PAGE_GUTTER;
            setWidth(fullScreen ? Math.min(inner, 960) : inner);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(box);
        return () => observer.disconnect();
    }, [fullScreen]);

    // The page under the reader's eye: the last page whose top has passed a
    // third of the way down the scroll box.
    const onScroll = () => {
        const box = scrollRef.current;
        if (!box) return;
        const line = box.scrollTop + box.clientHeight / 3;
        let page = 1;
        for (const el of box.querySelectorAll<HTMLElement>("[data-page]")) {
            if (el.offsetTop <= line) page = Number(el.dataset.page);
        }
        setCurrent(page);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
                ...(fullScreen
                    ? { bgcolor: colors.background }
                    : {
                          border: 1,
                          borderColor: "divider",
                          borderRadius: `${radius.lg}px`,
                          overflow: "hidden",
                          bgcolor: colors.lightGray,
                      }),
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                useFlexGap
                sx={{
                    px: 2,
                    py: 1,
                    bgcolor: colors.white,
                    borderBottom: 1,
                    borderColor: "divider",
                    // With an action, a phone puts the title on its own row so
                    // the button does not squeeze it to an ellipsis.
                    flexWrap: action ? { xs: "wrap", sm: "nowrap" } : "nowrap",
                }}
            >
                <Typography
                    sx={{ fontWeight: 700, color: colors.navy, flex: 1, flexBasis: action ? { xs: "100%", sm: 0 } : 0 }}
                    noWrap
                >
                    {title}
                </Typography>
                {action}
                {status === "ready" && (
                    <Typography variant="body2" sx={{ color: colors.mediumGray, whiteSpace: "nowrap" }}>
                        Page {current} of {numPages}
                    </Typography>
                )}
                {onExpand && (
                    <IconButton onClick={onExpand} aria-label="View full screen" size="small">
                        <FullscreenIcon />
                    </IconButton>
                )}
                <IconButton
                    component="a"
                    href={downloadUrl}
                    download
                    aria-label={`Download ${title}`}
                    size="small"
                >
                    <FileDownloadOutlinedIcon />
                </IconButton>
                {onClose && (
                    <IconButton onClick={onClose} aria-label="Exit full screen" size="small">
                        <CloseIcon />
                    </IconButton>
                )}
            </Stack>
            <Box
                ref={scrollRef}
                onScroll={onScroll}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    position: "relative",
                    px: `${PAGE_GUTTER}px`,
                    pt: 2,
                    // 20px under the last page (Bill, 2026-09-07).
                    pb: "20px",
                }}
            >
                {status === "loading" && (
                    <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {status === "error" && (
                    <Typography variant="body2" sx={{ p: 3, color: "text.secondary" }}>
                        This document could not be displayed here. Use the download button to open it.
                    </Typography>
                )}
                {doc && width > 0 && (
                    <Stack spacing={2} alignItems="center">
                        {Array.from({ length: numPages }, (_, i) => (
                            <Box key={i + 1} data-page={i + 1} sx={{ boxShadow: shadows.sm, lineHeight: 0 }}>
                                <PageCanvas doc={doc} page={i + 1} width={width} title={title} />
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    );
}

// One page, drawn at `width`. The same one-render-at-a-time rule as the
// paginated viewer: pdf.js refuses overlapping renders on a canvas, so a
// redraw cancels the previous task first.
function PageCanvas({
    doc,
    page,
    width,
    title,
}: {
    doc: PDFDocumentProxy;
    page: number;
    width: number;
    title: string;
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const taskRef = useRef<RenderTask | null>(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const previous = taskRef.current;
            previous?.cancel();
            const p = await doc.getPage(page);
            // Let a cancelled render finish before drawing, or both paint the
            // canvas (an upside-down second copy; PdfPages, 2026-10-30).
            await previous?.promise.catch(() => undefined);
            if (cancelled) return;
            const base = p.getViewport({ scale: 1 });
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const viewport = p.getViewport({ scale: (width / base.width) * dpr });
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${viewport.height / dpr}px`;
            const task = p.render({ canvas, viewport });
            taskRef.current = task;
            try {
                await task.promise;
            } catch {
                // Cancelled by a newer render; nothing to do.
            }
        })();
        return () => {
            cancelled = true;
            taskRef.current?.cancel();
        };
    }, [doc, page, width]);
    return (
        <canvas
            ref={canvasRef}
            role="img"
            aria-label={`${title}, page ${page} of ${doc.numPages}`}
            style={{ display: "block", maxWidth: "100%" }}
        />
    );
}
